'use client';

import { Box, Container, Grid2, Typography, useTheme } from '@mui/material';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import Image from 'next/image';
import Link from 'next/link';
import { ReactNode } from 'react';
import { UrlObject } from 'url';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { UploadFile } from '@/types';

interface LocationProps {
  heading: BlocksContent;
  body: BlocksContent;
  image: UploadFile;
}

export function Locations(props: LocationProps) {
  const theme = useTheme();

  return (
    <CustomBackground
      backgroundOptions={{
        BackgroundColor: 'purple-200',
        BackgroundOrbs: 'multi-3-left',
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          textAlign: 'center',
          position: 'relative',
          pt: 6,
          pb: 8,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            mx: 'auto',
            h1: {
              textAlign: 'center',
            },
          }}
        >
          <GlobalBlockRenderer content={props.heading || []} />
        </Box>

        <Grid2
          container
          sx={{
            textAlign: 'left',
            mt: 6,
          }}
          columnGap={10}
        >
          <Grid2 size={{ xs: 12, sm: 6 }} textAlign="center">
            <StrapiImage image={props.image} size="url" />
          </Grid2>

          <Grid2
            size={{ xs: 12, sm: 4 }}
            sx={{
              alignSelf: 'center',
              display: 'grid',
              alignItems: 'start',
              alignContent: 'start',
              justifyContent: 'space-around',
              columnGap: 1,
              gridTemplateColumns: {
                xs: '1fr 3fr',
                sm: '1fr',
              },
              img: {
                gridRow: 'span 2',
                marginTop: 2,
                justifySelf: 'center',
                display: {
                  xs: 'block',
                  sm: 'none',
                },
              },
              'h1,h2,h3,h4,h5,h6': {
                gridColumn: { xs: '2 / span 1', sm: '1' },
              },
            }}
          >
            <GlobalBlockRenderer
              content={props.body || []}
              overrides={{
                blocks: {
                  heading: ({ children }: { children?: ReactNode }) => {
                    return (
                      <>
                        <Image
                          src="/assets/marker.svg"
                          alt="Location marker"
                          width={50}
                          height={75}
                        />
                        <Typography
                          variant="h4"
                          component="h3"
                          sx={{
                            color: theme.palette.brand.primary,
                            mt: 3,
                            mb: -2,
                          }}
                        >
                          {children}
                        </Typography>
                      </>
                    );
                  },
                  link: ({ children, url }: { url: string | UrlObject; children?: ReactNode }) => (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'underline',
                        color: theme.palette.purple[800],
                        fontSize: theme.typography.small.fontSize,
                        fontWeight: 500,
                      }}
                    >
                      {children}
                    </Link>
                  ),
                },
              }}
            />
          </Grid2>
        </Grid2>
      </Container>
    </CustomBackground>
  );
}
